import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AGING_BUCKETS,
  LOAN_LABELS,
  PNL_RANGES,
  accountsToReview,
  agingReport,
  billAmount,
  billPartlyPaid,
  billStatusLabel,
  billsSummary,
  brokenPlaidItems,
  cardFigures,
  debtPosition,
  depositAccounts,
  fmtBillDate,
  isMoneyOut,
  loadAccounts,
  loadCashPosition,
  loadLoans,
  loadOpenBills,
  loadPlaidItems,
  loadPnl,
  loadPnlBreakdown,
  loadPnlRows,
  loadTransactions,
  loanFigures,
  loanPaymentsDue,
  netAfterScheduled,
  pageRange,
  pnlLines,
  pnlNotes,
  pnlQuery,
  scheduledAmount,
  syncBalances,
  syncBills,
  syncTransactions,
} from "../../features/finance/financeData";
import { fmt, timeAgo } from "../../utils/financeFormat";
import "../mobile.css";

// The phone Finance page (plan Phase 2): Money (cash, available, debt, AP, net after scheduled payments,
// bank accounts, cards, loans, broken bank links), Bills (AP aging by bucket and vendor), P&L (by month,
// with the desktop's breakdown) and Transactions (search, account, pages). Each tab's "Sync now" is the
// desktop's own sync for it. Plaid connect / reconnect / add accounts, retiring accounts and the charts
// stay on the desktop. Layout only: features/finance/financeData.js is the desktop page's own code.

const TABS = [["money", "Money"], ["bills", "Bills"], ["pnl", "P&L"], ["txns", "Transactions"]];

export default function FinanceScreen() {
  const [tab, setTab] = useState("money");
  return (
    <div className="jq-screen" data-testid="phone-finance">
      <div className="d-flex flex-wrap gap-2 mb-2" role="group" aria-label="View">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" className={`btn btn-sm ${tab === k ? "btn-secondary" : "btn-outline-secondary"}`} aria-pressed={tab === k} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      {tab === "money" && <Money />}
      {tab === "bills" && <Bills />}
      {tab === "pnl" && <Pnl />}
      {tab === "txns" && <Transactions />}
    </div>
  );
}

/** What a failed sync says: a gateway timeout usually means the sync is still running on the server. */
function syncFailure(err) {
  const msg = (err && err.message) || "";
  if (/^API 504\b/.test(msg)) return "Still running on the server (the answer took too long). Check again in a minute.";
  if (/^API \d+/.test(msg)) return `The sync was refused (${msg}).`;
  return "Couldn't reach JobQ. Check the connection and try again.";
}

/** "Sync now": the desktop's sync for this tab, then its re-read; says when it is done or refused. The
 *  re-read is the tab's LATEST one (a filter changed while the sync ran is kept). */
function SyncNow({ label = "Sync now", sync, after, testId }) {
  const [state, setState] = useState(null); // null | "running" | {ok} | {error}
  const afterRef = useRef(after);
  afterRef.current = after;
  const run = async () => {
    setState("running");
    try {
      await sync();
    } catch (err) {
      setState({ error: syncFailure(err) });
      return;
    }
    try {
      await afterRef.current();
      setState({ ok: true });
    } catch (err) {
      setState({ error: "Synced, but the page couldn't re-read. Pull it again in a moment." });
    }
  };
  return (
    <div className="mb-2" data-testid={testId}>
      <button type="button" className="btn btn-outline-primary w-100" disabled={state === "running"} onClick={run}>
        {state === "running" ? "Syncing…" : label}
      </button>
      {state?.ok && <div className="jq-muted mt-1" role="status">Synced just now.</div>}
      {state?.error && <div className="jq-note jq-note-danger mt-1" role="alert">{state.error}</div>}
    </div>
  );
}

const Kv = ({ label, value, sub, tone, testId }) => (
  <div data-testid={testId}>
    <dt>{label}</dt>
    <dd className={tone === "bad" ? "text-danger" : tone === "good" ? "text-success" : ""}>{value}{sub && <div className="jq-muted">{sub}</div>}</dd>
  </div>
);

function Money() {
  const [data, setData] = useState({ accounts: [], loans: [], loanTotals: null, cash: null, bills: [], items: [] });
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false); // every read failed: say so, never "no accounts"
  const reread = useCallback(async () => {
    // As the desktop: five reads, each on its own (one failing leaves the others).
    const results = await Promise.allSettled([loadAccounts(), loadLoans(), loadCashPosition(), loadOpenBills(), loadPlaidItems()]);
    const [accounts, loans, cash, bills, items] = results;
    setData((prev) => ({
      accounts: accounts.status === "fulfilled" ? accounts.value : prev.accounts,
      loans: loans.status === "fulfilled" ? loans.value.loans : prev.loans,
      loanTotals: loans.status === "fulfilled" ? loans.value.totals : prev.loanTotals,
      cash: cash.status === "fulfilled" ? cash.value : prev.cash,
      bills: bills.status === "fulfilled" ? bills.value : prev.bills,
      items: items.status === "fulfilled" ? items.value : prev.items,
    }));
    const allFailed = results.every((r) => r.status === "rejected");
    setFailed(allFailed);
    setLoaded(true);
    if (allFailed) throw new Error("unreadable");
  }, []);
  useEffect(() => { reread().catch(() => {}); }, [reread]);

  const { accounts, loans, loanTotals, cash, bills, items } = data;
  const debt = debtPosition({ accounts, loans, loanTotals });
  const ap = billsSummary(bills);
  const net = netAfterScheduled(cash, ap.scheduledTotal);
  const due = loanPaymentsDue(loans);
  const broken = brokenPlaidItems(items);
  const review = accountsToReview(items);
  const deposits = depositAccounts(accounts);

  if (!loaded) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;
  return (
    <>
      <SyncNow sync={syncBalances} after={reread} testId="fin-sync-balances" />
      {failed && <div className="jq-note jq-note-danger mb-2" role="alert">Couldn't reach JobQ: these figures couldn't be read.</div>}
      {broken.map((i) => (
        <div key={i.id} className="jq-note jq-note-warn mb-2" data-testid={`fin-broken-${i.id}`}>
          <div className="fw-semibold">{i.institution_name || "Bank"} — Connection lost</div>
          Bank login credentials have changed. Balances are stale until someone re-connects on a computer.{i.error_code ? ` (${i.error_code})` : ""}
        </div>
      ))}
      {review.map((i) => (
        <div key={i.id} className="jq-note jq-note-info mb-2" data-testid={`fin-review-${i.id}`}>
          <div className="fw-semibold">{i.institution_name || "Bank"} — accounts need review</div>
          {i.new_accounts_available_at ? "The bank has an account this connection isn't authorized for. " : ""}
          {i.missing_account_count > 0 ? `${i.missing_account_count} account${i.missing_account_count === 1 ? " is" : "s are"} no longer reported by the bank. ` : ""}
          Review it on a computer.
        </div>
      ))}
      {cash && (
        <dl className="jq-kv" data-testid="fin-cards">
          <Kv label="Total cash" value={fmt(Number(cash.total_cash) || 0)} />
          <Kv label="Available" value={fmt(Number(cash.total_available) || 0)} />
          <Kv label="Total debt" value={fmt(debt.totalDebt)} sub={`${loans.length} loans + ${debt.creditAccounts.length} card${debt.creditAccounts.length === 1 ? "" : "s"}`} />
          <Kv label="AP outstanding" value={fmt(ap.totalAP)} sub={`${bills.length} unpaid bills`} />
          {ap.overdueBills.length > 0 && <Kv label="Overdue bills" value={String(ap.overdueBills.length)} sub={fmt(ap.overdueAmount)} tone="bad" />}
          {due.upcoming.length > 0 && <Kv label="Loan payments due" value={fmt(due.total)} sub={`${due.upcoming.length} within 7 days`} tone="bad" />}
        </dl>
      )}
      {cash && (
        <section className="jq-card" data-testid="fin-net">
          <h2 className="jq-section-title">Net cash after scheduled payments</h2>
          <div>{fmt(Number(cash.total_available) || 0)} available − {fmt(ap.scheduledTotal)} scheduled</div>
          <div className={`fw-bold fs-5 ${net >= 0 ? "text-success" : "text-danger"}`}>{fmt(net)}</div>
          {ap.scheduledBills.map((b) => (
            <div key={b.id} className="jq-task-line">
              <span className="jq-task-line-main">{b.vendor_name}{b.invoice_number ? ` #${b.invoice_number}` : ""}{b.scheduled_payment_date ? ` · pays ${fmtBillDate(b.scheduled_payment_date)}` : ""}</span>
              <span>{fmt(scheduledAmount(b))}</span>
            </div>
          ))}
        </section>
      )}

      <h2 className="jq-section-title mt-3">Bank accounts</h2>
      {deposits.length === 0 && !failed && <div className="jq-list-state">No bank accounts connected.</div>}
      {deposits.map((a) => (
        <div key={a.id} className="jq-card" data-testid={`fin-account-${a.id}`}>
          <div className="jq-muted">{a.institution_name}</div>
          <div className="fw-semibold">{a.account_name}{a.mask ? ` ***${a.mask}` : ""}</div>
          <div className="fs-5 fw-bold">{fmt(Number(a.current_balance) || 0)}</div>
          {a.available_balance != null && Number(a.available_balance) !== Number(a.current_balance) && <div className="jq-muted">Available: {fmt(Number(a.available_balance))}</div>}
          <div className="jq-muted">{a.account_type}{a.last_synced ? ` · ${timeAgo(a.last_synced)}` : " · never synced"}</div>
          {a.plaid_missing_since && <div className="text-warning">No longer reported by the bank — balance frozen since {timeAgo(a.plaid_missing_since)}.</div>}
        </div>
      ))}

      {debt.creditAccounts.length > 0 && <h2 className="jq-section-title mt-3">Credit cards</h2>}
      {debt.creditAccounts.map((a) => {
        const c = cardFigures(a);
        return (
          <div key={a.id} className="jq-card" data-testid={`fin-card-${a.id}`}>
            <div className="fw-semibold">{c.label.name || a.account_name}{a.mask ? ` ***${a.mask}` : ""}</div>
            <div className="fs-5 fw-bold text-danger">{fmt(c.balance)}</div>
            {c.limit > 0 && <div className="jq-muted">{Math.round(c.pctUsed)}% of {fmt(c.limit)} used{c.available != null ? ` · ${fmt(c.available)} available` : ""}</div>}
            <div className="jq-muted">Plaid synced {a.last_synced ? timeAgo(a.last_synced) : "never"}</div>
          </div>
        );
      })}

      {(loans.length > 0 || debt.uniqueLoanAccounts.length > 0) && <h2 className="jq-section-title mt-3">Loans <span className="jq-muted">{fmt(debt.totalLoanBalance)}{debt.totalMonthly > 0 ? ` · ${fmt(debt.totalMonthly)}/mo` : ""}</span></h2>}
      {loans.map((loan) => {
        const f = loanFigures(loan, debt.loanAccounts);
        return (
          <div key={loan.id} className="jq-card" data-testid={`fin-loan-${loan.id}`}>
            <div className="jq-muted">{loan.lender} {loan.contract_number} · {f.isLOC ? "LOC" : "Term"}</div>
            <div className="fw-semibold">{loan.description}</div>
            <div className="fs-5 fw-bold">{fmt(f.displayBalance)}</div>
            {loan.notes && <div className={f.notesWarn ? "text-warning" : "jq-muted"}>{loan.notes}</div>}
            {f.stale && <div className="text-warning">Bank stopped reporting ***{f.stale.mask} — showing the recorded balance, not a live one.</div>}
            {f.isLOC && f.locAvailable > 0 && <div className="text-success">{fmt(f.locAvailable)} available credit{loan.utilization_pct != null ? ` · ${Math.round(loan.utilization_pct)}% drawn` : ""}</div>}
            {!f.isLOC && f.orig > 0 && <div className="jq-muted">{f.pctPaid.toFixed(1)}% paid of {fmt(f.orig)}</div>}
            <div className="jq-muted">
              {[
                loan.interest_rate > 0 ? `${(loan.interest_rate * 100).toFixed(2)}%` : null,
                loan.monthly_payment ? `${fmt(loan.monthly_payment)}/mo` : "Interest only",
                loan.maturity_date ? `matures ${loan.maturity_date}` : null,
              ].filter(Boolean).join(" · ")}
            </div>
            {f.nextPaymentText && <div className={f.isUrgent ? "text-danger fw-semibold" : "jq-muted"}>{f.nextPaymentText}</div>}
          </div>
        );
      })}
      {debt.uniqueLoanAccounts.map((a) => {
        const label = LOAN_LABELS[a.mask] || {};
        return (
          <div key={`acct-${a.id}`} className="jq-card" data-testid={`fin-loan-account-${a.id}`}>
            <div className="jq-muted">{a.institution_name}{label.type ? ` · ${label.type}` : ""}</div>
            <div className="fw-semibold">{label.name || a.account_name}{a.mask ? ` ***${a.mask}` : ""}</div>
            <div className="fs-5 fw-bold">{fmt(Math.abs(Number(a.current_balance) || 0))}</div>
            <div className="jq-muted">Plaid synced {a.last_synced ? timeAgo(a.last_synced) : "never"}</div>
          </div>
        );
      })}
    </>
  );
}

function Bills() {
  const [bills, setBills] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null); // `${bucket}::${vendor}` | bucket key
  const reread = useCallback(async () => {
    try {
      setBills(await loadOpenBills());
      setError(null);
    } catch (err) {
      setError("Couldn't reach JobQ: the bills couldn't be read.");
      setBills((b) => b || []);
      throw err;
    }
  }, []);
  useEffect(() => { reread().catch(() => {}); }, [reread]);
  const report = useMemo(() => agingReport(bills || []), [bills]);
  if (bills === null) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;
  return (
    <>
      <SyncNow label="Sync bills from Bill.com" sync={syncBills} after={reread} testId="fin-sync-bills" />
      {error && <div className="jq-note jq-note-danger mb-2" role="alert">{error}</div>}
      {!error && <div className="fw-semibold mb-2" data-testid="fin-ap-total">{fmt(report.grandTotal)} owed on {bills.length} bill{bills.length === 1 ? "" : "s"}</div>}
      {!error && bills.length === 0 && <div className="jq-list-state">No unpaid bills.</div>}
      {AGING_BUCKETS.map((b) => {
        const bucket = report.buckets[b.key];
        if (!bucket.count) return null;
        const isOpen = open === b.key || String(open || "").startsWith(`${b.key}::`);
        return (
          <section key={b.key} className="jq-card" data-testid={`fin-bucket-${b.key}`}>
            <button type="button" className="btn btn-link text-reset text-decoration-none w-100 text-start p-0 d-flex" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : b.key)}>
              <span className="fw-bold flex-grow-1">{b.label}</span>
              <span>{fmt(bucket.total)} · {bucket.count}</span>
            </button>
            {isOpen && report.vendorsByBucket[b.key].map((v) => {
              const vk = `${b.key}::${v.name}`;
              return (
                <div key={vk} className="mt-2">
                  <button type="button" className="btn btn-link text-reset text-decoration-none w-100 text-start p-0 d-flex" aria-expanded={open === vk} onClick={() => setOpen(open === vk ? b.key : vk)}>
                    <span className="flex-grow-1">{v.name}</span><span>{fmt(v.total)} · {v.count}</span>
                  </button>
                  {open === vk && v.bills.slice().sort((x, y) => billAmount(y) - billAmount(x)).map((bill) => (
                    <div key={bill.id} className="jq-task-line" data-testid={`fin-bill-${bill.id}`}>
                      <span className="jq-task-line-main">
                        #{bill.invoice_number || "—"} · {billStatusLabel(bill.status || "UNPAID")}
                        <div className="jq-muted">Billed {fmtBillDate(bill.bill_date)} · Due {fmtBillDate(bill.due_date)}</div>
                      </span>
                      <span>
                        {fmt(billAmount(bill))}
                        {billPartlyPaid(bill) && <div className="jq-muted">of {fmt(Number(bill.amount) || 0)}</div>}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </section>
        );
      })}
    </>
  );
}

function Pnl() {
  const [range, setRange] = useState("ytd");
  const [pnl, setPnl] = useState(undefined);
  const [drill, setDrill] = useState({}); // line -> {categories|error|loading}; `${line}|${key}` -> {rows|error|loading}
  const [open, setOpen] = useState(new Set());
  const query = pnlQuery(range);
  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    setPnl(undefined); setDrill({}); setOpen(new Set());
    loadPnl(query).then((d) => { if (mine === seq.current) setPnl(d || null); }).catch(() => { if (mine === seq.current) setPnl(null); });
  }, [query]);

  const toggle = (k, load) => {
    const next = new Set(open);
    if (next.has(k)) { next.delete(k); setOpen(next); return; }
    next.add(k); setOpen(next);
    if (drill[k] && !drill[k].error) return;
    const mine = seq.current;
    setDrill((d) => ({ ...d, [k]: { loading: true } }));
    load().then((v) => { if (mine === seq.current) setDrill((d) => ({ ...d, [k]: { value: v } })); })
      .catch((e) => { if (mine === seq.current) setDrill((d) => ({ ...d, [k]: { error: e.message } })); });
  };

  const months = pnl?.months || [];
  const totals = pnl?.totals || {};
  const lines = pnlLines(pnl);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 mb-2" role="group" aria-label="Period">
        {PNL_RANGES.map((r) => (
          <button key={r.key} type="button" className={`btn btn-sm ${range === r.key ? "btn-primary" : "btn-outline-primary"}`} aria-pressed={range === r.key} onClick={() => setRange(r.key)}>{r.label}</button>
        ))}
      </div>
      {pnl === undefined && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>}
      {pnl === null && <div className="jq-note jq-note-danger" role="alert">The P&amp;L couldn't be read.</div>}
      {pnl && pnl.basis === "cash" && pnlNotes(pnl).map((n) => <div key={n} className="jq-note jq-note-warn mb-2">{n}</div>)}
      {pnl && (
        <section className="jq-card" data-testid="fin-pnl-totals">
          <h2 className="jq-section-title">Total for the period</h2>
          {lines.map((ln) => {
            const body = (
              <>
                <span className={`flex-grow-1 ${ln.bold ? "fw-bold" : ""}`}>
                  {ln.drill && <span aria-hidden="true">{open.has(ln.drill) ? "▾ " : "▸ "}</span>}{ln.label}
                </span>
                <span className={ln.tone === "net" ? ((totals.net_profit || 0) >= 0 ? "text-success fw-bold" : "text-danger fw-bold") : ""}>{fmt(totals[ln.field] || 0)}</span>
              </>
            );
            return (
            <div key={ln.field}>
              {ln.drill ? (
                <button type="button" className={`btn btn-link text-reset text-decoration-none w-100 text-start p-0 d-flex ${ln.muted ? "jq-muted ps-3" : ""}`} aria-expanded={open.has(ln.drill)} onClick={() => toggle(ln.drill, () => loadPnlBreakdown(ln.drill, query))}>
                  {body}
                </button>
              ) : (
                <div className={`d-flex ${ln.muted ? "jq-muted ps-3" : ""}`}>{body}</div>
              )}
              {ln.drill && open.has(ln.drill) && <Breakdown line={ln.drill} state={drill[ln.drill]} drill={drill} open={open} onCat={(key) => toggle(`${ln.drill}|${key}`, () => loadPnlRows(ln.drill, key, query))} />}
            </div>
            );
          })}
        </section>
      )}
      {pnl && months.length === 0 && <div className="jq-list-state">No P&amp;L data for this period.</div>}
      {months.slice().reverse().map((m) => (
        <section key={m.month} className="jq-card" data-testid={`fin-pnl-month-${m.month}`}>
          <h2 className="jq-section-title">{m.month}</h2>
          {lines.filter((ln) => !ln.muted).map((ln) => (
            <div key={ln.field} className="d-flex"><span className="flex-grow-1">{ln.label}</span><span className={ln.tone === "net" ? (m.net_profit >= 0 ? "text-success fw-bold" : "text-danger fw-bold") : ""}>{fmt(m[ln.field] || 0)}</span></div>
          ))}
        </section>
      ))}
    </>
  );
}

function Breakdown({ line, state, drill, open, onCat }) {
  if (!state || state.loading) return <div className="jq-muted ps-3">Loading…</div>;
  if (state.error) return <div className="text-danger ps-3">Could not load the breakdown ({state.error}).</div>;
  if (!state.value.length) return <div className="jq-muted ps-3">Nothing in this range.</div>;
  return state.value.map((c) => {
    const k = `${line}|${c.key}`;
    const rows = drill[k];
    return (
      <div key={c.key} className="ps-3">
        <button type="button" className="btn btn-link text-reset text-decoration-none w-100 text-start p-0 d-flex" aria-expanded={open.has(k)} onClick={() => onCat(c.key)}>
          <span className="flex-grow-1"><span aria-hidden="true">{open.has(k) ? "▾ " : "▸ "}</span>{c.label}{c.needs_review ? " (needs a category)" : c.estimated ? " (estimated)" : ""}</span>
          <span>{fmt(c.total || 0)}</span>
        </button>
        {open.has(k) && (!rows || rows.loading ? <div className="jq-muted ps-3">Loading…</div>
          : rows.error ? <div className="text-danger ps-3">Could not load the rows ({rows.error}).</div>
            : rows.value.map((t) => (
              <div key={t.id} className="d-flex ps-3 jq-muted"><span className="flex-grow-1">{t.date} · {t.label}</span><span>{fmt(t.amount || 0)}</span></div>
            )))}
      </div>
    );
  });
}

function Transactions() {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null); // {rows, total} | {error}
  const [busy, setBusy] = useState(false); // a page / filter is being read (the old rows stay, dimmed)
  const seq = useRef(0);
  useEffect(() => { loadAccounts().then(setAccounts).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);
  const reread = useCallback(async () => {
    const mine = ++seq.current;
    setBusy(true);
    try {
      const r = await loadTransactions({ page, search: debounced, accountId });
      if (mine === seq.current) setResult((prev) => ({ rows: r.rows, total: r.total ?? prev?.total ?? 0 }));
    } catch (err) {
      if (mine === seq.current) setResult({ error: "Couldn't reach JobQ: the transactions couldn't be read." });
      throw err;
    } finally {
      if (mine === seq.current) setBusy(false);
    }
  }, [page, debounced, accountId]);
  useEffect(() => { reread().catch(() => {}); }, [reread]);
  const total = result?.total || 0;
  const { totalPages, rangeStart, rangeEnd } = pageRange(page, total);
  return (
    <>
      <SyncNow label="Sync transactions" sync={syncTransactions} after={reread} testId="fin-sync-txns" />
      <input type="search" className="form-control mb-2" placeholder="Merchant or description" aria-label="Merchant or description" value={search} onChange={(e) => setSearch(e.target.value)} />
      <select className="form-select mb-2" aria-label="Account" value={accountId} onChange={(e) => { setAccountId(e.target.value); setPage(0); }}>
        <option value="">All accounts</option>
        {accounts.map((a) => <option key={a.id} value={a.id}>{a.institution_name} {a.account_name} ***{a.mask}</option>)}
      </select>
      {result === null && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>}
      {result?.error && <div className="jq-note jq-note-danger" role="alert">{result.error}</div>}
      {result?.rows && result.rows.length === 0 && <div className="jq-list-state">No transactions.</div>}
      {busy && result?.rows && <div className="jq-muted" role="status">Loading…</div>}
      {(result?.rows || []).map((t) => (
        <div key={t.id} className={`jq-task-line ${busy ? "opacity-50" : ""}`} data-testid={`fin-txn-${t.id}`}>
          <span className="jq-task-line-main">
            {t.merchant_name || t.name}
            <div className="jq-muted">{t.date} · {[t.category, t.subcategory].filter(Boolean).join(" / ")}{t.pending ? " · Pending" : ""}</div>
          </span>
          <span className={isMoneyOut(t) ? "text-danger" : "text-success"}>{isMoneyOut(t) ? "-" : "+"}{fmt(Math.abs(Number(t.amount) || 0))}</span>
        </div>
      ))}
      {total > 0 && (
        <div className="d-flex align-items-center gap-2 mt-2">
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span className="flex-grow-1 text-center jq-muted" data-testid="fin-txn-range">{rangeStart}–{rangeEnd} of {total}</span>
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </>
  );
}
